/**
 * 认证 Provider：在应用启动时自动刷新用户信息。
 * 等待 Zustand persist 水合完成后再渲染子组件。
 * 注册全局 401 未授权处理器，在 token 过期时自动登出并跳转登录页。
 */

import type { ReactElement, ReactNode } from 'react'
import { useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import { syncRumUser } from '@/lib/rum'
import { router } from '@/router'
import { useAuthStore } from '@/stores'
import { setUnauthorizedHandler } from '@/services/api'

interface AuthProviderProps {
  children: ReactNode
}

/**
 * 处理 401 未授权响应：清除 store 状态并跳转登录页
 */
function handleUnauthorized(): void {
  // 获取 store 的 logout 方法来清除状态
  const { logout } = useAuthStore.getState()
  logout()

  // 使用 router.navigate 跳转到登录页
  void router.navigate('/login', { replace: true })
}

/**
 * 认证 Provider 组件
 * 等待 persist 水合完成，然后检查并刷新用户登录状态
 * @param props - 组件属性
 * @returns ReactElement
 */
export function AuthProvider({ children }: AuthProviderProps): ReactElement {
  const hasHydrated = useAuthStore((state) => state._hasHydrated)
  const token = useAuthStore((state) => state.token)
  const user = useAuthStore((state) => state.user)
  const refreshUser = useAuthStore((state) => state.refreshUser)

  // 注册全局 401 处理器
  useEffect((): void => {
    setUnauthorizedHandler(handleUnauthorized)
  }, [])

  useEffect((): void => {
    // 只在水合完成且有 token 时刷新用户信息
    if (hasHydrated && token !== null) {
      void refreshUser()
    }
  }, [hasHydrated, token, refreshUser])

  useEffect((): void => {
    if (!hasHydrated) {
      return
    }

    syncRumUser(user)
  }, [hasHydrated, user])

  // 水合未完成时显示加载状态
  if (!hasHydrated) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return <>{children}</>
}
